import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../ui/alert-dialog';
import { Plus, Pencil, Trash2, Search, Users, Truck } from 'lucide-react';
import { apiListPartners, apiCreatePartner, apiUpdatePartner, apiDeletePartner } from '../../erpApi';
import { toast } from 'sonner';

const empty = {
  kind: 'customer', business_name: '', vat_number: '', fiscal_code: '',
  address: '', city: '', zip_code: '', country: 'IT',
  phone: '', email: '', pec: '', sdi_code: '', category: '',
  payment_terms: '', notes: '', active: true, contacts: []
};

const PartnerForm = ({ initial, onSubmit, onCancel }) => {
  const isEdit = !!initial;
  const [f, setF] = useState(initial ? { ...initial } : empty);
  const submit = (e) => {
    e.preventDefault();
    if (!f.business_name.trim()) { toast.error('Ragione sociale obbligatoria'); return; }
    onSubmit(f);
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="partner-form">
      {isEdit && <div className="text-xs text-slate-500">Codice: <span className="font-mono font-semibold">{initial.code}</span></div>}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Tipo *</Label>
          <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })} disabled={isEdit}>
            <SelectTrigger data-testid="partner-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="customer">Cliente</SelectItem>
              <SelectItem value="supplier">Fornitore</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Categoria</Label>
          <Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="es. Wholesale" />
        </div>
      </div>
      <div className="space-y-1.5"><Label>Ragione sociale *</Label><Input data-testid="partner-name" value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} required /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>P. IVA</Label><Input value={f.vat_number} onChange={(e) => setF({ ...f, vat_number: e.target.value })} placeholder="IT12345678901" /></div>
        <div className="space-y-1.5"><Label>Codice Fiscale</Label><Input value={f.fiscal_code} onChange={(e) => setF({ ...f, fiscal_code: e.target.value })} /></div>
      </div>
      <div className="space-y-1.5"><Label>Indirizzo</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label>CAP</Label><Input value={f.zip_code} onChange={(e) => setF({ ...f, zip_code: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>Città</Label><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>Paese</Label><Input value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Telefono</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>PEC</Label><Input value={f.pec} onChange={(e) => setF({ ...f, pec: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>Codice SDI</Label><Input value={f.sdi_code} onChange={(e) => setF({ ...f, sdi_code: e.target.value })} placeholder="7 caratteri" /></div>
      </div>
      <div className="space-y-1.5"><Label>Condizioni pagamento</Label><Input value={f.payment_terms} onChange={(e) => setF({ ...f, payment_terms: e.target.value })} placeholder="es. Bonifico 30gg DF" /></div>
      <div className="space-y-1.5"><Label>Note</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
      <div className="flex items-center justify-between p-2 rounded border border-slate-200">
        <Label className="mb-0">Attivo</Label>
        <Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>
        <Button data-testid="partner-submit" type="submit" className="bg-blue-700 hover:bg-blue-800">{isEdit ? 'Salva' : 'Crea'}</Button>
      </DialogFooter>
    </form>
  );
};

const PartnersManager = () => {
  const [list, setList] = useState([]);
  const [kindFilter, setKindFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const reload = async () => {
    const params = {};
    if (kindFilter !== 'all') params.kind = kindFilter;
    if (search) params.search = search;
    const d = await apiListPartners(params);
    setList(d || []);
  };
  useEffect(() => { reload(); }, [kindFilter, search]); // eslint-disable-line
  const handleSubmit = async (data) => {
    try {
      if (editing) await apiUpdatePartner(editing.id, data); else await apiCreatePartner(data);
      toast.success(editing ? 'Anagrafica aggiornata' : 'Anagrafica creata');
      setDialogOpen(false); setEditing(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const handleDelete = async () => {
    if (!toDelete) return;
    try { await apiDeletePartner(toDelete.id); toast.success('Eliminato'); setToDelete(null); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  return (
    <div className="space-y-3" data-testid="partners-manager">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="h-9 w-[160px] text-sm" data-testid="partner-filter-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="customer">Solo clienti</SelectItem>
              <SelectItem value="supplier">Solo fornitori</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="partner-search" placeholder="Cerca per nome, codice, P.IVA..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-8 w-[300px] text-sm" />
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="partner-new" onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-blue-700 hover:bg-blue-800 gap-2 h-9"><Plus className="w-4 h-4" />Nuova anagrafica</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Modifica anagrafica' : 'Nuova anagrafica'}</DialogTitle>
              <DialogDescription>{editing ? `Codice: ${editing.code}` : 'Il codice (CL-XXXX o FO-XXXX) sarà generato in automatico.'}</DialogDescription>
            </DialogHeader>
            <PartnerForm initial={editing} onSubmit={handleSubmit} onCancel={() => { setDialogOpen(false); setEditing(null); }} />
          </DialogContent>
        </Dialog>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
          <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <div className="text-sm font-medium text-slate-700">Nessuna anagrafica</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Tipo</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Codice</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Ragione sociale</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">P. IVA</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Città</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Pagamento</th>
                <th className="text-center px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Attivo</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map(p => (
                <tr key={p.id} className={`hover:bg-slate-50 ${!p.active ? 'opacity-60' : ''}`}>
                  <td className="px-3 py-2">
                    {p.kind === 'customer'
                      ? <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium"><Users className="w-3 h-3" />Cliente</span>
                      : <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-medium"><Truck className="w-3 h-3" />Fornitore</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-700">{p.code}</td>
                  <td className="px-3 py-2 font-medium text-slate-900">{p.business_name}</td>
                  <td className="px-3 py-2 text-slate-600">{p.vat_number || '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{p.city || '—'}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs truncate max-w-[120px]">{p.payment_terms || '—'}</td>
                  <td className="px-3 py-2 text-center">{p.active ? '✓' : '–'}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing(p); setDialogOpen(true); }}><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => setToDelete(p)}><Trash2 className="w-3 h-3" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Eliminare {toDelete?.business_name}?</AlertDialogTitle><AlertDialogDescription>Le anagrafiche con documenti collegati non possono essere eliminate (disattivale invece).</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PartnersManager;
